import React, { useEffect, useMemo, useState } from "react";
import { collection, query, where, onSnapshot, doc, setDoc, deleteDoc, serverTimestamp } from "firebase/firestore";
import { firestore } from "../shared/FirebaseConfig";
import { useUser } from "../shared/UserContext";
import { teacherDisplayName } from "./signoutData";
import { avatarColor, initials } from "../shared/avatar";
import { activeClassOf } from "../shared/activeClass";
import { surnameKey } from "../shared/nameSort";

function todayKeyLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function fmtTime(ts) {
  const ms = ts && typeof ts.toMillis === "function" ? ts.toMillis() : ts;
  if (!ms) return "";
  return new Date(ms).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}
const STATUS_LABEL = { verified: "Verified", attempted: "Attempted" };
const STATUS_RANK = { need: 0, attempted: 1, verified: 2 };
const eventsOf = (c) =>
  c.events && c.events.length ? c.events : [{ status: c.status, at: c.contactedAt, by: c.contactedByName, note: c.note }];

// Admin morning board: every student school-wide not checked in today. Two layouts
// (By class / By student), status-ordered so the ones still needing a call float to
// the top and verified ones sink. Click a student → log an outcome (Present / Verified
// / Attempted) with a note; each outcome appends a timestamped event so repeat call
// attempts are kept. Stored under schools/{schoolId}/absenceContacts/{date}_{sid}
// (sameSchool read/write — no rules change), live across admins. Only classes that
// have STARTED attendance today are counted.
export default function SchoolAbsentView() {
  const { schoolId, uid, teacherName, displayName } = useUser();
  const meName = teacherName || displayName || "Admin";
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState({});
  const [userPrefs, setUserPrefs] = useState({}); // uid -> classPrefs
  const [presentByClass, setPresentByClass] = useState({});
  const [contacts, setContacts] = useState({});
  const [popup, setPopup] = useState(null); // { student, cid }
  const [noteInput, setNoteInput] = useState("");
  const [view, setView] = useState("class");        // class | student
  const [studentSort, setStudentSort] = useState("class"); // class | name
  const todayKey = useMemo(() => todayKeyLocal(), []);

  useEffect(() => {
    if (!schoolId) return;
    const unsubs = [
      onSnapshot(query(collection(firestore, "classes"), where("schoolId", "==", schoolId)),
        snap => setClasses(snap.docs.map(d => ({ id: d.id, ...d.data() })))),
      onSnapshot(query(collection(firestore, "students"), where("schoolId", "==", schoolId)),
        snap => setStudents(snap.docs.map(d => ({ id: d.id, ...d.data() })))),
      onSnapshot(query(collection(firestore, "users"), where("schoolId", "==", schoolId)),
        snap => {
          const m = {}; const p = {};
          snap.docs.forEach(d => { const data = d.data(); m[d.id] = teacherDisplayName(data); p[d.id] = data.classPrefs || {}; });
          setTeachers(m); setUserPrefs(p);
        }),
      onSnapshot(query(collection(firestore, "schools", schoolId, "absenceContacts"), where("date", "==", todayKey)),
        snap => { const m = {}; snap.docs.forEach(d => { const x = d.data(); m[x.studentId] = x; }); setContacts(m); },
        err => console.error("[SchoolAbsentView] contacts:", err)),
    ];
    return () => unsubs.forEach(fn => fn());
  }, [schoolId, todayKey]);

  const classMap = useMemo(() => {
    const m = {};
    classes.forEach(c => { m[c.id] = { className: c.className || "Class", teacherId: c.teacherId, isArchived: c.isArchived === true, isHomeroom: c.isHomeroom === true, isSpecials: c.classType === "specials", attendanceEnabled: c.attendanceEnabled }; });
    return m;
  }, [classes]);
  const activeClassIds = useMemo(() => new Set(classes.filter(c => !c.isArchived).map(c => c.id)), [classes]);
  const homeroomClassIds = useMemo(() => new Set(classes.filter(c => c.isHomeroom && !c.isArchived).map(c => c.id)), [classes]);
  const specialsClassIds = useMemo(() => new Set(classes.filter(c => c.classType === "specials").map(c => c.id)), [classes]);

  // Attendance on/off is the OWNER's per-class pref (classPrefs), falling back to a
  // class field, default on. A class with attendance off is excluded entirely.
  const attendanceOn = (cid) => {
    const owner = classMap[cid]?.teacherId;
    const pref = userPrefs[owner]?.[cid]?.attendanceEnabled;
    if (pref === false) return false;
    if (pref === true) return true;
    return classMap[cid]?.attendanceEnabled !== false;
  };

  // Same grouping as the Emergency board (shared helper) — honors the homeroom flag
  // so the absent board and the emergency board can't disagree. See shared/activeClass.js.
  const homeOf = (s) => activeClassOf(s, activeClassIds, homeroomClassIds, specialsClassIds);

  const homeClassIds = useMemo(() => {
    const set = new Set();
    students.forEach(s => { const h = homeOf(s); if (h && attendanceOn(h)) set.add(h); });
    return [...set];
  }, [students, activeClassIds, userPrefs, classMap]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!homeClassIds.length) { setPresentByClass({}); return; }
    const unsubs = homeClassIds.map(cid =>
      onSnapshot(
        collection(firestore, "classes", cid, "attendance", todayKey, "students"),
        snap => setPresentByClass(prev => ({ ...prev, [cid]: new Set(snap.docs.map(d => d.id)) })),
        err => console.error("[SchoolAbsentView] attendance:", err),
      )
    );
    return () => unsubs.forEach(fn => fn());
  }, [homeClassIds.join(","), todayKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Flat list of all absent students (home class started attendance, not present).
  const absentList = useMemo(() => {
    const out = [];
    students.forEach(s => {
      const h = homeOf(s);
      if (!h || !attendanceOn(h)) return;
      const present = presentByClass[h];
      if (!present || present.size === 0) return;
      if (present.has(s.id)) return;
      out.push({ s, cid: h, className: classMap[h]?.className || "Class", status: contacts[s.id]?.status || "need" });
    });
    return out;
  }, [students, presentByClass, contacts, classMap, userPrefs, activeClassIds]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalAbsent = absentList.length;
  const totalToContact = absentList.filter(x => x.status === "need").length;

  // Home classes whose today attendance has no check-ins yet — likely haven't taken
  // attendance. (size 0 once the listener has reported; undefined = still loading.)
  const notStarted = useMemo(() =>
    homeClassIds
      .filter(cid => presentByClass[cid] && presentByClass[cid].size === 0)
      .map(cid => classMap[cid]?.className || "Class")
      .sort((a, b) => a.localeCompare(b)),
  [homeClassIds, presentByClass, classMap]); // eslint-disable-line react-hooks/exhaustive-deps

  // By-class sections: need-to-contact first, then logged (attempted before verified).
  const sections = useMemo(() => {
    const byClass = {};
    absentList.forEach(x => { (byClass[x.cid] = byClass[x.cid] || []).push(x); });
    return Object.keys(byClass)
      .map(cid => {
        const items = byClass[cid];
        const sortByName = (a, b) => surnameKey(a.s).localeCompare(surnameKey(b.s));
        return {
          cid,
          className: classMap[cid]?.className || "Class",
          teacher: teachers[classMap[cid]?.teacherId] || "—",
          notContacted: items.filter(x => x.status === "need").sort(sortByName),
          handled: items.filter(x => x.status !== "need")
            .sort((a, b) => STATUS_RANK[a.status] - STATUS_RANK[b.status] || sortByName(a, b)),
        };
      })
      .sort((a, b) => a.className.localeCompare(b.className));
  }, [absentList, classMap, teachers]);

  // By-student buckets, sorted by class or A–Z.
  const buckets = useMemo(() => {
    const cmp = (a, b) => studentSort === "class"
      ? (a.className.localeCompare(b.className) || surnameKey(a.s).localeCompare(surnameKey(b.s)))
      : surnameKey(a.s).localeCompare(surnameKey(b.s));
    const pick = (st) => absentList.filter(x => x.status === st).sort(cmp);
    return { need: pick("need"), attempted: pick("attempted"), verified: pick("verified") };
  }, [absentList, studentSort]);

  const openPopup = (student, cid) => { setNoteInput(""); setPopup({ student, cid }); };
  const closePopup = () => setPopup(null);

  const markPresent = async (s, cid) => {
    try {
      await setDoc(doc(firestore, "classes", cid, "attendance", todayKey, "students", s.id), { timestamp: serverTimestamp() });
      await deleteDoc(doc(firestore, "schools", schoolId, "absenceContacts", `${todayKey}_${s.id}`)).catch(() => {});
    } catch (err) { console.error("mark present:", err); }
    closePopup();
  };
  const logContact = async (s, cid, status) => {
    const existing = contacts[s.id];
    const events = [...(existing?.events || []), { status, at: new Date().toISOString(), by: meName, note: noteInput.trim() }];
    try {
      await setDoc(doc(firestore, "schools", schoolId, "absenceContacts", `${todayKey}_${s.id}`), {
        studentId: s.id, studentName: s.studentName || "", date: todayKey, classId: cid,
        status, note: noteInput.trim(), contactedBy: uid, contactedByName: meName,
        contactedAt: serverTimestamp(), events,
      });
    } catch (err) { console.error("log contact:", err); }
    closePopup();
  };
  const clearContact = async (s) => {
    try { await deleteDoc(doc(firestore, "schools", schoolId, "absenceContacts", `${todayKey}_${s.id}`)); }
    catch (err) { console.error("clear contact:", err); }
    closePopup();
  };

  // Detailed tile (By student view): avatar + name + events. Colored by status.
  const Tile = ({ x, showClass }) => {
    const { s, cid, status } = x;
    const name = s.studentName || "Unnamed Student";
    const c = contacts[s.id];
    return (
      <button className={`absent-tile is-${status}`} onClick={() => openPopup(s, cid)}>
        <span className="att-avatar" style={{ background: avatarColor(name) }}>{initials(name)}</span>
        <span className="absent-tile-main">
          <span className="absent-tile-name">{name}</span>
          {showClass && <span className="absent-tile-class">{x.className}</span>}
          <span className="absent-tile-sub">#{s.studentId}</span>
          {status !== "need" && c && (
            <span className="absent-events">
              {eventsOf(c).map((e, i) => (
                <span key={i} className={`absent-event ${e.status}`}>
                  {STATUS_LABEL[e.status] || "Logged"}{fmtTime(e.at) ? ` ${fmtTime(e.at)}` : ""}{e.by ? ` · ${e.by}` : ""}{e.note ? ` — “${e.note}”` : ""}
                </span>
              ))}
            </span>
          )}
        </span>
      </button>
    );
  };

  const bucketSection = (label, cls, items) => items.length > 0 && (
    <div className="absent-bucket">
      <div className={`absent-bucket-head ${cls}`}>{label} · {items.length}</div>
      <div className="absent-grid">
        {items.map(x => <Tile key={x.s.id} x={x} showClass />)}
      </div>
    </div>
  );

  return (
    <div style={{ marginTop: "var(--space-3)", width: "100%" }}>
      <div className="att-header">
        <h2 style={{ margin: 0 }}>Not checked in · school-wide</h2>
        <span className="absent-summary">{totalToContact} to contact · {totalAbsent} absent</span>
      </div>

      <div className="absent-toolbar">
        <div className="absent-seg">
          <button className={view === "class" ? "active" : ""} onClick={() => setView("class")}>By class</button>
          <button className={view === "student" ? "active" : ""} onClick={() => setView("student")}>By student</button>
        </div>
        {view === "student" && (
          <div className="absent-seg">
            <button className={studentSort === "class" ? "active" : ""} onClick={() => setStudentSort("class")}>Sort: Class</button>
            <button className={studentSort === "name" ? "active" : ""} onClick={() => setStudentSort("name")}>A–Z</button>
          </div>
        )}
      </div>

      {notStarted.length > 0 && (
        <div className="absent-notstarted">
          ⏳ Haven't started attendance yet: {notStarted.join(" · ")}
        </div>
      )}

      {totalAbsent === 0 ? (
        <div className="att-allhere">Everyone who's been taken is here 🎉</div>
      ) : view === "student" ? (
        <div style={{ width: "100%" }}>
          {bucketSection("Still to contact", "need", buckets.need)}
          {bucketSection("Attempted — follow up", "attempted", buckets.attempted)}
          {bucketSection("Verified", "verified", buckets.verified)}
        </div>
      ) : (
        <div className="absent-classes">
          {sections.map(sec => (
            <div key={sec.cid} className="card absent-class">
              <div className="absent-class-head">
                <h3 style={{ margin: 0 }}>{sec.className}</h3>
                <span className="absent-class-meta">
                  {sec.teacher} · {sec.notContacted.length} to contact{sec.handled.length ? ` · ${sec.handled.length} logged` : ""}
                </span>
              </div>
              {sec.notContacted.length > 0 && (
                <div className="absent-grid">
                  {sec.notContacted.map(x => <Tile key={x.s.id} x={x} />)}
                </div>
              )}
              {sec.notContacted.length === 0 && (
                <p className="absent-allclear">All accounted for ✓</p>
              )}
              {sec.handled.length > 0 && (
                <>
                  <div className="absent-divider"><span>Logged · {sec.handled.length}</span></div>
                  <div className="absent-grid">
                    {sec.handled.map(x => <Tile key={x.s.id} x={x} />)}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {popup && (
        <div className="modal-overlay" onClick={closePopup}>
          <div className="contact-modal" onClick={(e) => e.stopPropagation()}>
            <h2 style={{ margin: "0 0 2px" }}>{popup.student.studentName}</h2>
            <p style={{ color: "var(--color-secondary)", fontSize: 14, margin: "0 0 14px" }}>
              {classMap[popup.cid]?.className || "Class"}
            </p>

            <label className="contact-note-label">Note (optional)</label>
            <textarea
              value={noteInput}
              onChange={(e) => setNoteInput(e.target.value)}
              placeholder="e.g. mom says back tomorrow"
              style={{ width: "100%", height: 56, fontSize: 14, boxSizing: "border-box" }}
            />
            <p className="contact-hint">
              Choose an outcome below to log it (your note is saved with it). “Attempted” can be logged more than once — each gets a timestamp.
            </p>

            <div className="contact-actions">
              <button onClick={() => markPresent(popup.student, popup.cid)} style={{ background: "var(--color-success)", borderColor: "var(--color-success)" }}>
                ✓ Present — mark here
              </button>
              <button className="btn-secondary" onClick={() => logContact(popup.student, popup.cid, "verified")}>
                Verified absent
              </button>
              <button className="btn-secondary" onClick={() => logContact(popup.student, popup.cid, "attempted")}>
                Attempted — no answer
              </button>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12 }}>
              {contacts[popup.student.id]
                ? <button className="btn-ghost" onClick={() => clearContact(popup.student)}>Clear log</button>
                : <span />}
              <button className="btn-ghost" onClick={closePopup}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
